// https://material-ui-pickers.dev/

import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { FormLabel } from '@material-ui/core';

import { compose } from 'redux';
import { withStyles } from '@material-ui/styles';
import withHelperText from "../../hocs/with_helper_text.js";
import withGuid from "../../hocs/with_guid.js";

import { MuiPickersUtilsProvider,
         KeyboardDatePicker
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from "moment";

import { InputVariantStandard, InputVariantOutlined, InputVariantFilled,
         InputLabelDisplayModeInside, InputLabelDisplayModeAbove
} from "./constants.js.jsx";

import I18n from "../i18n.js";

const styles = theme => ({
});

moment.locale(I18n.locale);

/**
 * Callback for inputs
 * @callback DateTimeField~onChange
 * @param {string} value input's new value
 */

/**
 * Component for input password. Already includes HOCs {@link WithGuid}, {@link WithHelperText}
 * @name DateTimeField
 * @param {object} props React component properties
 * @param {string} [props.value] Field's value
 * @param {number} [props.utcOffset = 0] Offset from UTC-time for display datetime in correct for user timezone
 * @param {boolean} [props.withTime = false] Need display and edit time in field
 * @param {string} [props.format = "DD.MM.YYYY"] Display value format
 * @param {string} [props.serverDateFormat = "YYYY-MM-DD"] Format for send date to server
 * @param {string} [props.serverDateTimeFormat = "YYYY-MM-DDTHH:mm:ss"] Format for send datetime to server
 * @param {string} [props.displayName] Field's display name
 * @param {boolean} [props.hasErrors = false] Does errors exists on the field?
 * @param {Array.<string>} [props.errors = []] Error messages array
 * @param {boolean} [props.readOnly = false] Does field is read only?
 * @param {string} [props.className] CSS-class of button
 * @param {string} [props.variant = "outlined"] View variant for text field stylization. Available values:
 *  - "standard"
 *  - "outlined"
 *  - "filled"
 *
 * Explanation of variants: {@link https://material-ui.com/components/text-fields/#textfield}
 *
 * NOTE: for providing variant, please, import necessary variant as constant from file "components/base/inputs/constants.js.jsx"
 * @param {string} [props.displayNamePosition = "inside"] Position of field's display name: inside the field with floating style or above. Available values:
 *  - "inside"
 *  - "above"
 *
 * NOTE: for providing position, please, import necessary position as constant from file "components/base/inputs/constants.js.jsx"
 * @param {DateTimeField~onChange} [props.onChange] On change handler
 * @example
 * return (
 *   <DateTimeField className={ "awesome-class" }
 *                  onChange = { (value) => {
 *                    // Some actions
 *                  } }
 *                  value = { "2020-01-01" } />
 * )
*/
class DateTimePicker extends React.Component {
  constructor(props) {
    super(props);

    this.state = {

    }

    this.onChange = this.onChange.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    let value;

    let { value: propsValue = null } = props;
    let { defaultValue, value: stateValue = null } = state;

    if((!defaultValue && propsValue) || defaultValue !== propsValue) {
      defaultValue = propsValue;
      value = propsValue;
    }
    else {
      value = stateValue;
    }

    return {
      value: value,
      defaultValue: defaultValue
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.state.value !== nextState.value ||
           this.props.displayName !== nextProps.displayName ||
           this.props.hasError !== nextProps.hasError ||
           this.props.readOnly !== nextProps.readOnly;
  }

  onChange(newDate) {
    const { value } = this.state;

    const { onChange } = this.props;

    const { defaultHours, defaultMinutes, defaultSeconds } = this.props;

    const newDateIsValid = newDate && newDate.isValid();

    if(!value && newDateIsValid) {
      if(defaultHours) {
        newDate = newDate.hours(defaultHours);
      }
      if(defaultMinutes) {
        newDate = newDate.minutes(defaultMinutes);
      }
      if(defaultSeconds) {
        newDate = newDate.seconds(defaultSeconds);
      }
    }


    let newValue = newDateIsValid ? DateTimePicker.subtractOffset(newDate, this.props) : newDate;
    this.setState({
      value: newValue
    });

    if(onChange && (newValue === null || newDateIsValid)) {
      onChange(newValue);
    }
  }

  render() {
    const { classes, className, guid, readOnly } = this.props;

    const {
      name, displayName,
      variant, format,
      withTime, displayNamePosition, hasError,
      disableAutoComplete,
      minDate, maxDate
    } = this.props;

    let { value } = this.state;

    return <React.Fragment>
      {
        displayNamePosition === InputLabelDisplayModeAbove &&
        <FormLabel htmlFor = { guid }
                   error = { hasError } >
          { `${displayName}:` }
        </FormLabel>
      }
      <MuiPickersUtilsProvider utils = { MomentUtils }
                               locale = { I18n.locale } >
        {
          !withTime &&
          <KeyboardDatePicker className = { clsx(classes.root, className) }
                              id = { guid }
                              fullWidth
                              onChange = { this.onChange }
                              value = { DateTimePicker.addOffset(value, this.props) }
                              name = { disableAutoComplete ? guid : name }
                              error = { hasError }
                              label = { displayNamePosition === InputLabelDisplayModeInside ? displayName : undefined }
                              inputVariant = { variant }
                              format = { format }
                              clearable
                              disabled = { readOnly }
                              minDate = { minDate }
                              maxDate = { maxDate }
                              invalidDateMessage = { I18n.t("component.date_time_field.invalid_date") }
                              maxDateMessage = { I18n.t("component.date_time_field.too_big_date", {date: moment.utc(maxDate).format("DD.MM.YYYY")}) }
                              minDateMessage = { I18n.t("component.date_time_field.too_small_date", {date: moment.utc(minDate).format("DD.MM.YYYY")}) }
                              okLabel = { I18n.t("action.ok") }
                              todayLabel = { I18n.t("shared.today") }
                              clearLabel = { I18n.t("action.clear") }
                              cancelLabel = { I18n.t("action.cancel") }/>
        }
        {/* <KeyboardDateTimePicker  /> */}
      </MuiPickersUtilsProvider>
    </React.Fragment>
  }

  static addOffset(value, props) {

    if(!value) {
      return value;
    }

    const { withTime, utcOffset, serverDateFormat, serverDateTimeFormat } = props;

    if(!withTime) {
      return moment(value).format(serverDateFormat);
    }
    let dateValue = moment.utc(value);
    dateValue = dateValue.hours(dateValue.hours() + utcOffset);

    return dateValue.format(serverDateTimeFormat);
  }

  static subtractOffset(value, props) {
    if(!value) {
      return value;
    }

    const { withTime, utcOffset, serverDateFormat, serverDateTimeFormat } = props;

    if(!withTime) {
      return value.format(serverDateFormat);
    }

    let dateValue = value.hours(value.hours() - utcOffset);
    return dateValue.format(serverDateTimeFormat);;
  }
}

DateTimePicker.propTypes = {
  variant: PropTypes.oneOf([InputVariantStandard, InputVariantOutlined, InputVariantFilled]),
  displayNamePosition: PropTypes.oneOf([InputLabelDisplayModeAbove, InputLabelDisplayModeInside]),
  readOnly: PropTypes.bool,
  utcOffset: PropTypes.number,
  withTime: PropTypes.bool,
  format: PropTypes.string,
  serverDateFormat: PropTypes.string,
  serverDateTimeFormat: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string
};

DateTimePicker.defaultProps = {
  variant: InputVariantOutlined,
  displayNamePosition: InputLabelDisplayModeInside,
  underline: true,
  readOnly: false,

  utcOffset: 0,
  withTime: false,
  format: "DD.MM.YYYY",
  serverDateFormat: "YYYY-MM-DD",
  serverDateTimeFormat: "YYYY-MM-DDTHH:mm:ss",
  minDate: "1900-01-01",
  maxDate: "2100-12-31"
}

export default compose(withGuid, withHelperText, withStyles(styles))(DateTimePicker);
